import { type FunctionComponent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type OrderGetOrderDetailsOrderItemReadDto,
  type OrderItemDetailsReadDto,
  WorkflowPostponedActivityReadDtoActivityEnum,
} from "@/config/apiClient";
import {
  OrderItemLogActionTypeEnum,
  OrderItemStatusEnum,
} from "@/config/apiClient.types";
import { useIsOMS } from "@/hooks/useIsOMS";
import { ModalAddDeliveryLabel } from "@/modals/ModalAddDeliveryLabel";
import { ModalHDF } from "@/modals/ModalHDF/ModalHDF";
import { ModalPreMediaClientRequestDetailsForm } from "@/modals/ModalPreMediaClientRequestDetailsForm";
import { ModalPreMediaPropositionFileForm } from "@/modals/ModalPreMediaPropositionFileForm";
import { ModalPurchaseOrderForm } from "@/modals/ModalPurchaseOrderForm";
import { ModalUploadDeliveryNoteForm } from "@/modals/ModalUploadDeliveryNoteForm";
import { ModalUploadProofForm } from "@/modals/ModalUploadProofForm";
import { ModalValidateDeliveryLabel } from "@/modals/ModalValidateDeliveryLabel";
import { ModalWorkflowLoopBack } from "@/modals/ModalWorkflowLoopBack";
import { type TransitionData } from "@/types/transitionData";

// TOREFACTO :
// - Destructure props in the component to avoid passing props to the component
// - Add modal provider : pass the modal to open as a prop and necessary props to open it
// - Reduce complexity of the HOC

export type WithOrderItemActionsComponentProps = {
  orderItem?: OrderItemDetailsReadDto | OrderGetOrderDetailsOrderItemReadDto;
  orderItems: (
    | OrderItemDetailsReadDto
    | OrderItemDetailsReadDto["order"]["OrderItem"][number]
    | OrderGetOrderDetailsOrderItemReadDto
  )[];
  onError?: () => void;
  isLoading: boolean;
  actions: {
    [K in string]: (
      orderItem?: OrderItemDetailsReadDto | OrderGetOrderDetailsOrderItemReadDto
    ) => void;
  };
};

export const withOrderItemActions =
  <T,>(
    Component: FunctionComponent<T & WithOrderItemActionsComponentProps>,
    params: {
      showDecideOnQuoteButton: boolean;
      showDecideOnProofButton: boolean;
      showDecideOnPreMediaBriefButton: boolean;
      showAddPreMediaPropositionButton: boolean;
      showDecideOnModificationRequestButton: boolean;
    }
  ) =>
  (props: Omit<T & WithOrderItemActionsComponentProps, "actions">) => {
    // State
    const [isUploadProofModalOpen, setIsUploadProofModalOpen] = useState(false);
    const [isGraphicModalOpen, setIsGraphicModalOpen] =
      useState<boolean>(false);
    const [isAddDeliveryNoteModalOpen, setIsAddDeliveryNoteModalOpen] =
      useState(false);
    const [isAddPurchaseOrderModalOpen, setIsAddPurchaseOrderModalOpen] =
      useState<boolean>(false);
    const [
      addPreMediaPropositionFileModalOpen,
      setAddPreMediaPropositionFileModalOpen,
    ] = useState(false);
    const [
      isPreMediaClientRequestDetailsModalOpen,
      setIsPreMediaClientRequestDetailsModalOpen,
    ] = useState(false);
    const [
      isCancelAllCurrentQuotesModalOpen,
      setIsCancelAllCurrentQuotesModalOpen,
    ] = useState(false);
    const [isCancelProofModalOpen, setIsCancelProofModalOpen] = useState(false);
    const [isAddDeliveryLabelModalOpen, setIsAddDeliveryLabelModalOpen] =
      useState(false);
    const [
      isDecideOnDeliveryLabelModalOpen,
      setDecideOnDeliveryLabelModalOpen,
    ] = useState(false);

    // Hooks
    const navigate = useNavigate();
    const isOMS = useIsOMS();
    const hasAModificationRequest =
      props.orderItem?.Workflow.PostponedActivities.some(
        (postponedActivity) =>
          postponedActivity.activity ===
          WorkflowPostponedActivityReadDtoActivityEnum.DecideOnModificationRequest
      );

    const actions = useMemo(() => {
      const base: {
        [K in
          | WorkflowPostponedActivityReadDtoActivityEnum
          | "DECIDE_ON_MODIFICATION_REQUEST"]?: (
          orderItem?:
            | OrderItemDetailsReadDto
            | OrderGetOrderDetailsOrderItemReadDto
        ) => void;
      } =
        props.orderItem?.status === OrderItemStatusEnum.CANCELLED
          ? {}
          : {
              ...(isOMS
                ? {
                    CANCEL_QUOTE: !hasAModificationRequest
                      ? () => setIsCancelAllCurrentQuotesModalOpen(true)
                      : undefined,
                    CANCEL_PROOF: !hasAModificationRequest
                      ? () => setIsCancelProofModalOpen(true)
                      : undefined,
                    SHARE_RETRO_PLANNING: (orderItem) =>
                      void navigate(
                        `/oms/orders/${orderItem?.orderId}?tab=retroplanning`
                      ),
                    CHECK_RETRO_PLANNING: (orderItem) =>
                      orderItem?.status === "IN_PROGRESS"
                        ? void navigate(
                            `${isOMS ? "/oms" : ""}/orders/${orderItem?.orderId}?tab=retroplanning`
                          )
                        : undefined,
                    ADD_DELIVERY_LABEL: !hasAModificationRequest
                      ? () => setIsAddDeliveryLabelModalOpen(true)
                      : undefined,
                  }
                : {
                    CHECK_RETRO_PLANNING: (orderItem) =>
                      void navigate(
                        `${isOMS ? "/oms" : ""}/orders/${orderItem?.orderId}?tab=retroplanning`
                      ),
                    DECIDE_ON_DELIVERY_LABEL: !hasAModificationRequest
                      ? () => setDecideOnDeliveryLabelModalOpen(true)
                      : undefined,
                  }),

              ADD_QUOTE: !hasAModificationRequest
                ? (orderItem) =>
                    void navigate(
                      `/oms/orders/${orderItem?.orderId}/order-items/${orderItem?.id}/add-quote`
                    )
                : undefined,
              SEND_QUOTE: !hasAModificationRequest
                ? (orderItem) =>
                    void navigate(
                      `/oms/orders/${orderItem?.orderId}/order-items/${orderItem?.id}/add-quote`
                    )
                : undefined,

              ADD_PRE_MEDIA_BRIEF: () =>
                setIsPreMediaClientRequestDetailsModalOpen(true),
              ...(params.showDecideOnQuoteButton === true &&
              !hasAModificationRequest
                ? {
                    DECIDE_ON_QUOTE: (orderItem) =>
                      void navigate(
                        `/orders/${orderItem?.orderId}/order-items/${orderItem?.id}`
                      ),
                  }
                : {}),
              ...(params.showDecideOnPreMediaBriefButton === true
                ? {
                    DECIDE_ON_PRE_MEDIA_BRIEF: (orderItem) =>
                      void navigate(
                        `/oms/orders/${orderItem?.orderId}/order-items/${orderItem?.id}`
                      ),
                  }
                : {}),
              ...(params.showAddPreMediaPropositionButton === true
                ? {
                    ADD_PRE_MEDIA_PROPOSITION: (orderItem) =>
                      void navigate(
                        `/oms/orders/${orderItem?.orderId}/order-items/${orderItem?.id}`
                      ),
                  }
                : {}),
              DECIDE_ON_PRE_MEDIA_PROPOSITION:
                props.orderItem?.sandboxItem.preMediaPropositionsFiles &&
                props.orderItem?.sandboxItem.preMediaPropositionsFiles.length >
                  0 &&
                props.orderItem?.sandboxItem.preMediaPropositionsFiles?.[0]
                  .externalUrl
                  ? () => {
                      if (props.orderItem) {
                        window.open(
                          props.orderItem.sandboxItem
                            .preMediaPropositionsFiles?.[0].externalUrl,
                          "_blank"
                        );
                      }
                    }
                  : undefined,

              ADD_HDF: !hasAModificationRequest
                ? () => setIsGraphicModalOpen(true)
                : undefined,
              ADD_PROOF: !hasAModificationRequest
                ? () => setIsUploadProofModalOpen(true)
                : undefined,
              ...(params.showDecideOnProofButton === true &&
              !hasAModificationRequest
                ? {
                    DECIDE_ON_PROOF: (orderItem) =>
                      void navigate(
                        `/orders/${orderItem?.orderId}/order-items/${orderItem?.id}`
                      ),
                  }
                : {}),

              ADD_PURCHASE_ORDER: !hasAModificationRequest
                ? () => setIsAddPurchaseOrderModalOpen(true)
                : undefined,

              ADD_DELIVERY_NOTE: !hasAModificationRequest
                ? () => {
                    setIsAddDeliveryNoteModalOpen(true);
                  }
                : undefined,

              ...(params.showDecideOnModificationRequestButton === true
                ? {
                    DECIDE_ON_MODIFICATION_REQUEST: (orderItem) =>
                      void navigate(
                        `/oms/orders/${orderItem?.orderId}/order-items/${orderItem?.id}/edit`
                      ),
                  }
                : {}),
            };

      return base;
    }, [
      isOMS,
      navigate,
      hasAModificationRequest,
      props.orderItem,
      params.showAddPreMediaPropositionButton,
      params.showDecideOnModificationRequestButton,
      params.showDecideOnPreMediaBriefButton,
      params.showDecideOnProofButton,
      params.showDecideOnQuoteButton,
    ]) as {
      [K in
        | WorkflowPostponedActivityReadDtoActivityEnum
        | "DECIDE_ON_MODIFICATION_REQUEST"]?: (
        orderItem?:
          | OrderItemDetailsReadDto
          | OrderGetOrderDetailsOrderItemReadDto
      ) => void;
    };

    return (
      <>
        <Component
          {...(props as T & WithOrderItemActionsComponentProps)}
          actions={actions}
        />

        {!props.isLoading && props.orderItem && (
          <>
            <ModalWorkflowLoopBack
              isOpen={isCancelAllCurrentQuotesModalOpen}
              setIsOpen={setIsCancelAllCurrentQuotesModalOpen}
              orderItem={props.orderItem}
              activity={
                WorkflowPostponedActivityReadDtoActivityEnum.CancelQuote
              }
            />

            <ModalWorkflowLoopBack
              isOpen={isCancelProofModalOpen}
              setIsOpen={setIsCancelProofModalOpen}
              orderItem={props.orderItem}
              activity={
                WorkflowPostponedActivityReadDtoActivityEnum.CancelProof
              }
            />

            <ModalHDF
              isOpen={isGraphicModalOpen}
              onOpenChange={(toOpen) => setIsGraphicModalOpen(!!toOpen)}
              orderItem={props.orderItem}
              key={props.orderItem.id}
            />

            <ModalUploadDeliveryNoteForm
              key={`upload-delivery-note-${isAddDeliveryNoteModalOpen}`}
              orderItems={props.orderItems}
              orderItem={props.orderItem}
              isOpen={isAddDeliveryNoteModalOpen}
              setIsOpen={setIsAddDeliveryNoteModalOpen}
              onError={props.onError}
            />

            <ModalUploadProofForm
              key={`upload-proof-${isUploadProofModalOpen}`}
              orderId={props.orderItem?.orderId}
              orderItemId={props.orderItem?.id}
              isOpen={isUploadProofModalOpen}
              setOpenState={setIsUploadProofModalOpen}
              onError={props.onError}
            />

            <ModalPurchaseOrderForm
              isOpen={isAddPurchaseOrderModalOpen}
              setOpenState={setIsAddPurchaseOrderModalOpen}
              orderId={props.orderItem.orderId}
            />

            <ModalAddDeliveryLabel
              isOpen={isAddDeliveryLabelModalOpen}
              onOpenChange={setIsAddDeliveryLabelModalOpen}
              orderId={props.orderItem.orderId}
              orderItemId={props.orderItem.id}
            />

            <ModalValidateDeliveryLabel
              isOpen={isDecideOnDeliveryLabelModalOpen}
              orderItem={props.orderItem}
              onOpenChange={setDecideOnDeliveryLabelModalOpen}
            />

            <ModalPreMediaPropositionFileForm
              orderItem={props.orderItem}
              isOpen={addPreMediaPropositionFileModalOpen}
              onCloseModal={() => setAddPreMediaPropositionFileModalOpen(false)}
            />

            {!isOMS && (
              <ModalPreMediaClientRequestDetailsForm
                setOpenState={(isOpen: boolean) =>
                  setIsPreMediaClientRequestDetailsModalOpen(isOpen)
                }
                isOpen={isPreMediaClientRequestDetailsModalOpen}
                orderItem={props.orderItem}
                csmComment={
                  (
                    props.orderItem.OrderLog.find(
                      (orderLog) =>
                        orderLog.actionType ===
                        OrderItemLogActionTypeEnum.PRE_MEDIA_REFUSED
                    )?.transitionData as TransitionData
                  )?.comment || ""
                }
              />
            )}
          </>
        )}
      </>
    );
  };
